import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from './api';
import type {
  AdminLocation,
  AdminOrderDetail,
  AdminProduct,
  AdminUser,
  ApiEnvelope,
  DashboardData,
  Notification,
  OrderDetail,
  OrderSummary,
  Paginated,
  ProductSummary,
  Supplier,
  Vendor,
  VendorDocument,
} from './types';

export const qk = {
  products: ['products'] as const,
  vendors: ['vendors'] as const,
  orders: ['orders'] as const,
  notifications: ['notifications'] as const,
  adminProducts: ['admin', 'products'] as const,
  adminSuppliers: ['admin', 'suppliers'] as const,
  adminLocations: ['admin', 'locations'] as const,
  adminUsers: ['admin', 'users'] as const,
  adminDashboard: ['admin', 'dashboard'] as const,
  adminDocuments: ['admin', 'documents'] as const,
  adminOrders: ['admin', 'orders'] as const,
};

export function useAdminOrder(id: string | undefined) {
  return useQuery({
    queryKey: [...qk.adminOrders, id],
    queryFn: () => apiFetch<ApiEnvelope<AdminOrderDetail>>(`/api/admin/orders/${id}`),
    enabled: Boolean(id),
  });
}

export function useProducts() {
  return useQuery({
    queryKey: qk.products,
    queryFn: () => apiFetch<ApiEnvelope<ProductSummary[]>>('/api/products'),
  });
}

// Vendors for a location with their next deadline. Refreshed every minute so a
// passed deadline rolls over to the next delivery day.
export function useVendors(locationId: string | undefined) {
  return useQuery({
    queryKey: [...qk.vendors, locationId],
    queryFn: () => apiFetch<ApiEnvelope<Vendor[]>>(`/api/vendors?locationId=${locationId}`),
    enabled: Boolean(locationId),
    refetchInterval: 60_000,
  });
}

export function useOrders(page = 1) {
  return useQuery({
    queryKey: [...qk.orders, page],
    queryFn: () => apiFetch<Paginated<OrderSummary>>(`/api/orders?page=${page}&pageSize=20`),
  });
}

export function useOrderDetail(id: string | undefined) {
  return useQuery({
    queryKey: [...qk.orders, id],
    queryFn: () => apiFetch<ApiEnvelope<OrderDetail>>(`/api/orders/${id}`),
    enabled: Boolean(id),
  });
}

export function useOrderDocuments(id: string | undefined) {
  return useQuery({
    queryKey: [...qk.orders, id, 'documents'],
    queryFn: () => apiFetch<ApiEnvelope<VendorDocument[]>>(`/api/orders/${id}/documents`),
    enabled: Boolean(id),
  });
}

export function useNotifications() {
  return useQuery({
    queryKey: qk.notifications,
    queryFn: () => apiFetch<ApiEnvelope<Notification[]>>('/api/notifications'),
  });
}

export function useAdminProducts(search?: string) {
  return useQuery({
    queryKey: [...qk.adminProducts, search ?? ''],
    queryFn: () => apiFetch<Paginated<AdminProduct>>(`/api/admin/products${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  });
}

export function useAdminSuppliers() {
  return useQuery({
    queryKey: qk.adminSuppliers,
    queryFn: () => apiFetch<ApiEnvelope<Supplier[]>>('/api/admin/suppliers'),
  });
}

export function useAdminLocations() {
  return useQuery({
    queryKey: qk.adminLocations,
    queryFn: () => apiFetch<ApiEnvelope<AdminLocation[]>>('/api/admin/locations'),
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: qk.adminUsers,
    queryFn: () => apiFetch<ApiEnvelope<AdminUser[]>>('/api/admin/users'),
  });
}

export function useAdminDashboard() {
  return useQuery({
    queryKey: qk.adminDashboard,
    queryFn: () => apiFetch<ApiEnvelope<DashboardData>>('/api/admin/dashboard'),
  });
}

export interface CreateOrderInput {
  locationId: string;
  notes?: string;
  lines: Array<{ productId: string; formatId?: string; quantity: number }>;
}

export function useSubmitOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrderInput) =>
      apiFetch<ApiEnvelope<{ orderId: string; purchaseOrderIds: string[] }>>('/api/orders', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.orders });
      void qc.invalidateQueries({ queryKey: qk.notifications });
    },
  });
}

export async function markNotificationRead(id: string): Promise<void> {
  // Empty JSON body: the API rejects a JSON content-type with no body.
  await apiFetch(`/api/notifications/${id}/read`, { method: 'POST', body: '{}' });
}

// Dismiss a dashboard alert (marks the notification read) and refresh.
export function useDismissAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.adminDashboard });
      void qc.invalidateQueries({ queryKey: qk.notifications });
    },
  });
}

export function safeMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}
